
import React from "react";
import PageLayout from "@/components/Layout/PageLayout";
import { useToast } from "@/hooks/use-toast";
import { 
  FileText,
  UserCircle,
  Calendar,
  Pill,
  Clock,
  CreditCard 
} from "lucide-react";
import { HeroBanner } from "@/components/ui/hero-banner";
import {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent
} from "@/components/ui/card";
import { 
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from "@/components/ui/tabs";
import { useUserSession } from '@/hooks/useUserSession';
import AccessDenied from "@/components/AccessDenied";

const PatientPage: React.FC = () => {


const { user, isLoaded } = useUserSession();

// if (!user || user.role !== 'patient') return <AccessDenied />;
  return (
    <PageLayout>
      <HeroBanner
        title="Patient Portal"
        description="Access your health records, schedule appointments, and manage medications"
        backgroundColor="bg-gradient-to-r from-blue-700 to-blue-900"
      />

      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg p-4 shadow mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="bg-blue-100 text-blue-600 rounded-full p-4">
              <UserCircle size={32} />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Welcome, John Doe</h2>
              <p className="text-gray-600">Last login: Today at 9:30 AM</p>
            </div>
            <div className="ml-auto flex flex-col sm:flex-row gap-2 mt-4 md:mt-0">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                Schedule Appointment
              </button>
              <button className="px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 transition-colors">
                Message Doctor
              </button>
            </div>
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-1 md:grid-cols-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
            <TabsTrigger value="records">Medical Records</TabsTrigger>
            <TabsTrigger value="medications">Medications</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
            <TabsTrigger value="insurance">Insurance</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Upcoming Appointments</CardTitle>
                  <CardDescription>Your scheduled medical visits</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="flex p-3 border rounded-lg hover:bg-gray-50">
                        <div className="mr-4 flex-shrink-0">
                          <div className="bg-blue-100 text-blue-600 rounded-full h-12 w-12 flex items-center justify-center">
                            <Calendar size={20} />
                          </div>
                        </div>
                        <div>
                          <p className="font-medium">
                            {["Annual Checkup", "Cardiology Follow-up", "Allergy Consultation"][i]}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(Date.now() + (i + 1) * 7 * 24 * 60 * 60 * 1000).toLocaleDateString()} at {["9:00 AM", "1:30 PM", "11:15 AM"][i]}
                          </p>
                          <p className="text-sm text-gray-500">
                            Dr. {["Smith", "Johnson", "Williams"][i]}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div className="mt-2 text-center">
                      <button className="text-blue-600 font-medium text-sm">
                        View All Appointments
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Current Medications</CardTitle>
                  <CardDescription>Your prescribed medications</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      {name: "Lisinopril", dosage: "10mg", frequency: "Once daily"},
                      {name: "Metformin", dosage: "500mg", frequency: "Twice daily"},
                      {name: "Simvastatin", dosage: "20mg", frequency: "Once daily, evening"}
                    ].map((med, i) => (
                      <div key={i} className="flex p-3 border rounded-lg hover:bg-gray-50">
                        <div className="mr-4 flex-shrink-0">
                          <div className="bg-blue-100 text-blue-600 rounded-full h-12 w-12 flex items-center justify-center">
                            <Pill size={20} />
                          </div>
                        </div>
                        <div>
                          <p className="font-medium">{med.name}</p>
                          <p className="text-sm text-gray-500">
                            {med.dosage} - {med.frequency}
                          </p>
                          <p className="text-sm text-gray-500">
                            Refills remaining: {3 - i}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div className="mt-2 text-center">
                      <button className="text-blue-600 font-medium text-sm">
                        View All Medications
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>Recent updates to your health record</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative pl-6 border-l border-gray-200">
                    {[
                      {
                        icon: <FileText size={16} />,
                        title: "Lab Results Added",
                        description: "Blood work results from your recent visit",
                        time: "2 days ago"
                      },
                      {
                        icon: <Calendar size={16} />,
                        title: "Appointment Scheduled",
                        description: "Follow-up with Dr. Johnson",
                        time: "1 week ago"
                      },
                      {
                        icon: <Pill size={16} />,
                        title: "Prescription Refilled",
                        description: "Lisinopril 10mg - 30 day supply",
                        time: "2 weeks ago"
                      },
                      {
                        icon: <FileText size={16} />,
                        title: "Visit Summary Added",
                        description: "Annual physical examination notes",
                        time: "1 month ago"
                      },
                      {
                        icon: <CreditCard size={16} />,
                        title: "Insurance Claim Processed",
                        description: "Claim #12345 for office visit on Jan 15",
                        time: "1 month ago"
                      }
                    ].map((item, i) => (
                      <div key={i} className="mb-6 relative">
                        <div className="absolute -left-10 mt-1.5 flex items-center justify-center w-5 h-5 bg-blue-100 rounded-full text-blue-600">
                          {item.icon}
                        </div>
                        <h4 className="font-medium">{item.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                        <p className="text-xs text-gray-500 mt-1 flex items-center">
                          <Clock size={12} className="mr-1" /> {item.time}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <CardTitle>Manage Your Appointments</CardTitle>
                <CardDescription>View, schedule, and manage your medical appointments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3">Upcoming Appointments</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="px-4 py-3 text-left text-sm font-medium">Date & Time</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Provider</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Purpose</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...Array(3)].map((_, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              {new Date(Date.now() + (i + 1) * 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}, {["9:00 AM", "1:30 PM", "11:15 AM"][i]}
                            </td>
                            <td className="px-4 py-3 text-sm">Dr. {["Smith", "Johnson", "Williams"][i]}</td>
                            <td className="px-4 py-3 text-sm">
                              {["Annual Checkup", "Cardiology Follow-up", "Allergy Consultation"][i]}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {["Main Campus", "West Wing", "Specialty Clinic"][i]}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex space-x-2">
                                <button className="text-blue-600 hover:text-blue-800 font-medium">Reschedule</button>
                                <button className="text-red-600 hover:text-red-800 font-medium">Cancel</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-3">Past Appointments</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="px-4 py-3 text-left text-sm font-medium">Date & Time</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Provider</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Purpose</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...Array(5)].map((_, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              {new Date(Date.now() - (i + 1) * 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}, {["10:00 AM", "2:30 PM", "9:15 AM", "3:45 PM", "11:30 AM"][i]}
                            </td>
                            <td className="px-4 py-3 text-sm">Dr. {["Anderson", "Davis", "Miller", "Wilson", "Taylor"][i]}</td>
                            <td className="px-4 py-3 text-sm">
                              {["Annual Physical", "Blood Pressure Check", "Vaccination", "Dermatology Consult", "Eye Exam"][i]}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex space-x-2">
                                <button className="text-blue-600 hover:text-blue-800 font-medium">View Summary</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="records">
            <Card>
              <CardHeader>
                <CardTitle>Your Medical Records</CardTitle>
                <CardDescription>Access your complete health history</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Test Results</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {[
                          { name: "Complete Blood Count", date: "03/12/2025" },
                          { name: "Lipid Panel", date: "03/12/2025" },
                          { name: "Metabolic Panel", date: "03/12/2025" },
                          { name: "Urinalysis", date: "01/05/2025" },
                          { name: "A1C Test", date: "12/10/2024" }
                        ].map((test, i) => (
                          <div key={i} className="flex justify-between items-center border-b pb-2">
                            <div>
                              <p className="font-medium">{test.name}</p>
                              <p className="text-sm text-gray-500">{test.date}</p>
                            </div>
                            <button className="text-blue-600 text-sm font-medium">View</button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Visit Summaries</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {[
                          { name: "Annual Physical", doctor: "Dr. Smith", date: "03/12/2025" },
                          { name: "Cardiology Follow-up", doctor: "Dr. Johnson", date: "01/20/2025" },
                          { name: "Urgent Care Visit", doctor: "Dr. Davis", date: "12/05/2024" },
                          { name: "Allergy Consultation", doctor: "Dr. Wilson", date: "11/15/2024" }
                        ].map((visit, i) => (
                          <div key={i} className="flex justify-between items-center border-b pb-2">
                            <div>
                              <p className="font-medium">{visit.name}</p>
                              <p className="text-sm text-gray-500">{visit.doctor} - {visit.date}</p>
                            </div>
                            <button className="text-blue-600 text-sm font-medium">View</button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Immunizations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {[
                          { name: "Influenza Vaccine", date: "09/15/2024" },
                          { name: "COVID-19 Booster", date: "06/22/2024" },
                          { name: "Tetanus Booster", date: "05/10/2022" },
                          { name: "Pneumococcal Vaccine", date: "03/05/2021" }
                        ].map((vaccine, i) => (
                          <div key={i} className="flex justify-between items-center border-b pb-2">
                            <div>
                              <p className="font-medium">{vaccine.name}</p>
                              <p className="text-sm text-gray-500">{vaccine.date}</p>
                            </div>
                            <button className="text-blue-600 text-sm font-medium">Details</button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-3">Medical History</h3>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium mb-2">Allergies</h4>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded mr-2">Severe</span>
                          <span>Penicillin</span>
                        </div>
                        <div className="flex items-center">
                          <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded mr-2">Moderate</span>
                          <span>Shellfish</span>
                        </div>
                        <div className="flex items-center">
                          <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded mr-2">Moderate</span>
                          <span>Pollen</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium mb-2">Chronic Conditions</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Hypertension (diagnosed 2018)</li>
                        <li>Type 2 Diabetes (diagnosed 2019)</li>
                        <li>Asthma (since childhood)</li>
                      </ul>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium mb-2">Surgical History</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Appendectomy (2010)</li>
                        <li>Knee Arthroscopy (2015)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="medications">
            <Card>
              <CardHeader>
                <CardTitle>Medication Management</CardTitle>
                <CardDescription>View and manage your prescribed medications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3">Current Medications</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="px-4 py-3 text-left text-sm font-medium">Medication</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Dosage</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Instructions</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Prescribing Doctor</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Refills</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          {name: "Lisinopril", dosage: "10mg", instructions: "Once daily", doctor: "Dr. Smith", refills: 2},
                          {name: "Metformin", dosage: "500mg", instructions: "Twice daily with meals", doctor: "Dr. Johnson", refills: 3},
                          {name: "Simvastatin", dosage: "20mg", instructions: "Once daily, evening", doctor: "Dr. Smith", refills: 1},
                          {name: "Albuterol", dosage: "90mcg", instructions: "As needed for breathing", doctor: "Dr. Williams", refills: 5}
                        ].map((med, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium">{med.name}</td>
                            <td className="px-4 py-3 text-sm">{med.dosage}</td>
                            <td className="px-4 py-3 text-sm">{med.instructions}</td>
                            <td className="px-4 py-3 text-sm">{med.doctor}</td>
                            <td className="px-4 py-3 text-sm">{med.refills}</td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex space-x-2">
                                <button className="text-blue-600 hover:text-blue-800 font-medium">Info</button>
                                <button className="text-green-600 hover:text-green-800 font-medium">Refill</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-3">Medication History</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="px-4 py-3 text-left text-sm font-medium">Medication</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Dosage</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Date Range</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Reason Stopped</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          {name: "Amoxicillin", dosage: "500mg", dates: "Jan 5 - Jan 15, 2024", reason: "Completed treatment"},
                          {name: "Hydrochlorothiazide", dosage: "25mg", dates: "Jun 2021 - Dec 2023", reason: "Switched to Lisinopril"},
                          {name: "Prednisone", dosage: "10mg", dates: "Nov 10 - Nov 24, 2023", reason: "Completed treatment"}
                        ].map((med, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{med.name}</td>
                            <td className="px-4 py-3 text-sm">{med.dosage}</td>
                            <td className="px-4 py-3 text-sm">{med.dates}</td>
                            <td className="px-4 py-3 text-sm">{med.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h3 className="text-lg font-medium mb-2">Medication Resources</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 bg-white rounded border">
                      <h4 className="font-medium">Pharmacy Information</h4>
                      <p className="text-sm text-gray-600 mt-1">Main Street Pharmacy</p>
                      <p className="text-sm text-gray-600">123 Main St, Anytown, USA</p>
                      <p className="text-sm text-gray-600">(555) 123-4567</p>
                    </div>
                    <div className="p-3 bg-white rounded border">
                      <h4 className="font-medium">Medication Guides</h4>
                      <ul className="list-disc list-inside text-sm text-blue-600 mt-1 space-y-1">
                        <li><a href="#" className="hover:underline">Understanding Your Medications</a></li>
                        <li><a href="#" className="hover:underline">Side Effects Management</a></li>
                        <li><a href="#" className="hover:underline">Medication Interactions</a></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="billing">
            <Card>
              <CardHeader>
                <CardTitle>Billing & Payments</CardTitle>
                <CardDescription>Manage your healthcare expenses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Current Balance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">$250.00</div>
                      <p className="text-xs text-muted-foreground mt-2">Last updated: Today</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Payment Due</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">$125.00</div>
                      <p className="text-xs text-muted-foreground mt-2">Due by: April 15, 2025</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Year-to-Date</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">$750.00</div>
                      <p className="text-xs text-muted-foreground mt-2">Deductible: $350.00 remaining</p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3">Recent Statements</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Service</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Provider</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Total Charges</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Insurance Paid</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Your Responsibility</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          {date: "03/12/2025", service: "Annual Checkup", provider: "Dr. Smith", total: "$350.00", insurance: "$280.00", responsibility: "$70.00", status: "Due"},
                          {date: "02/05/2025", service: "Lab Work", provider: "Medical Labs Inc.", total: "$225.00", insurance: "$180.00", responsibility: "$45.00", status: "Pending Insurance"},
                          {date: "01/20/2025", service: "Cardiology Consult", provider: "Dr. Johnson", total: "$450.00", insurance: "$360.00", responsibility: "$90.00", status: "Paid"},
                          {date: "12/05/2024", service: "Urgent Care", provider: "City Urgent Care", total: "$275.00", insurance: "$220.00", responsibility: "$55.00", status: "Paid"}
                        ].map((bill, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{bill.date}</td>
                            <td className="px-4 py-3 text-sm">{bill.service}</td>
                            <td className="px-4 py-3 text-sm">{bill.provider}</td>
                            <td className="px-4 py-3 text-sm">{bill.total}</td>
                            <td className="px-4 py-3 text-sm">{bill.insurance}</td>
                            <td className="px-4 py-3 text-sm">{bill.responsibility}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${bill.status === "Due" ? "bg-red-100 text-red-800" : bill.status === "Pending Insurance" ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}>
                                {bill.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex space-x-2">
                                <button className="text-blue-600 hover:text-blue-800 font-medium">View</button>
                                {bill.status === "Due" && (
                                  <button className="text-green-600 hover:text-green-800 font-medium">Pay</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Payment Methods</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="p-3 border rounded-lg flex items-center">
                          <div className="mr-3">
                            <CreditCard className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">Visa ending in 4567</p>
                            <p className="text-sm text-gray-500">Expires 05/27</p>
                          </div>
                          <div className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Default</div>
                        </div>
                        <div className="p-3 border rounded-lg flex items-center">
                          <div className="mr-3">
                            <CreditCard className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">Mastercard ending in 8901</p>
                            <p className="text-sm text-gray-500">Expires 11/26</p>
                          </div>
                        </div>
                        <button className="text-blue-600 font-medium text-sm">Add Payment Method</button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>Billing Preferences</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">Paper Statements</p>
                            <p className="text-sm text-gray-500">Receive physical copies by mail</p>
                          </div>
                          <div className="w-10 h-5 bg-gray-200 rounded-full relative">
                            <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">Email Notifications</p>
                            <p className="text-sm text-gray-500">Receive billing updates by email</p>
                          </div>
                          <div className="w-10 h-5 bg-blue-600 rounded-full relative">
                            <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">Auto-Pay</p>
                            <p className="text-sm text-gray-500">Automatically pay bills when due</p>
                          </div>
                          <div className="w-10 h-5 bg-blue-600 rounded-full relative">
                            <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="insurance">
            <Card>
              <CardHeader>
                <CardTitle>Insurance Information</CardTitle>
                <CardDescription>View your coverage details and claims</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Primary Insurance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Provider:</span>
                          <span className="font-medium">Blue Cross Blue Shield</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Plan:</span>
                          <span className="font-medium">PPO Gold</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Member ID:</span>
                          <span className="font-medium">XYZ123456789</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Group Number:</span>
                          <span className="font-medium">G12345</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Effective Date:</span>
                          <span className="font-medium">01/01/2025</span>
                        </div>
                        <div className="mt-4">
                          <button className="text-blue-600 font-medium text-sm">View Insurance Card</button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>Coverage Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Annual Deductible:</span>
                          <span className="font-medium">$1,500 ($350 remaining)</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Out-of-Pocket Max:</span>
                          <span className="font-medium">$4,000 ($2,850 remaining)</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Primary Care Visit:</span>
                          <span className="font-medium">$25 copay</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Specialist Visit:</span>
                          <span className="font-medium">$50 copay</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Emergency Room:</span>
                          <span className="font-medium">$250 copay</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Prescription Drugs:</span>
                          <span className="font-medium">$10/$30/$50</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3">Recent Claims</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="px-4 py-3 text-left text-sm font-medium">Date of Service</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Provider</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Service Type</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Claim #</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Billed Amount</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Insurance Paid</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          {date: "03/12/2025", provider: "Dr. Smith", service: "Annual Checkup", claim: "CL987654", billed: "$350.00", paid: "$280.00", status: "Processed"},
                          {date: "02/05/2025", provider: "Medical Labs Inc.", service: "Lab Work", claim: "CL987123", billed: "$225.00", paid: "$180.00", status: "In Process"},
                          {date: "01/20/2025", provider: "Dr. Johnson", service: "Cardiology", claim: "CL986789", billed: "$450.00", paid: "$360.00", status: "Processed"},
                          {date: "12/05/2024", provider: "City Urgent Care", service: "Urgent Care", claim: "CL980123", billed: "$275.00", paid: "$220.00", status: "Processed"}
                        ].map((claim, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{claim.date}</td>
                            <td className="px-4 py-3 text-sm">{claim.provider}</td>
                            <td className="px-4 py-3 text-sm">{claim.service}</td>
                            <td className="px-4 py-3 text-sm">{claim.claim}</td>
                            <td className="px-4 py-3 text-sm">{claim.billed}</td>
                            <td className="px-4 py-3 text-sm">{claim.paid}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${claim.status === "Processed" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                                {claim.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <button className="text-blue-600 hover:text-blue-800 font-medium">Details</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>In-Network Providers</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">Primary Care</p>
                            <p className="text-sm text-gray-500">Find in-network primary care physicians</p>
                          </div>
                          <button className="text-blue-600 font-medium text-sm">Search</button>
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">Specialists</p>
                            <p className="text-sm text-gray-500">Find in-network specialists</p>
                          </div>
                          <button className="text-blue-600 font-medium text-sm">Search</button>
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">Hospitals</p>
                            <p className="text-sm text-gray-500">Find in-network hospitals</p>
                          </div>
                          <button className="text-blue-600 font-medium text-sm">Search</button>
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">Pharmacies</p>
                            <p className="text-sm text-gray-500">Find in-network pharmacies</p>
                          </div>
                          <button className="text-blue-600 font-medium text-sm">Search</button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>Coverage Resources</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="p-3 border rounded-lg">
                          <h4 className="font-medium">Policy Documents</h4>
                          <ul className="list-disc list-inside text-sm text-blue-600 mt-1 space-y-1">
                            <li><a href="#" className="hover:underline">Summary of Benefits</a></li>
                            <li><a href="#" className="hover:underline">Certificate of Coverage</a></li>
                            <li><a href="#" className="hover:underline">Prescription Formulary</a></li>
                          </ul>
                        </div>
                        <div className="p-3 border rounded-lg">
                          <h4 className="font-medium">Insurance Contact Information</h4>
                          <p className="text-sm text-gray-600 mt-1">Customer Service: (800) 123-4567</p>
                          <p className="text-sm text-gray-600">Claims Department: (800) 234-5678</p>
                          <p className="text-sm text-gray-600">Prior Authorization: (800) 345-6789</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default PatientPage;
